export type PortId = string
const D = console.log

/* physical I/O port belonging to ProPatch */
export class ChannelPort {
    // CREATE from port index
    static toPortId (port:number,arch:IArch) : PortId | undefined {
      return arch.ports.toPortId(port)
    }

    // TRANSLATE port ID to index
    static fromPortId (id:string|number,arch:IArch) : number | undefined {
      return arch.ports.fromPortId(id)
    }

    // VALIDATE port ID format (arch-agnostic syntax check)
    // Back-panel banks A-L (PP200R uses up to L); front-panel FP1-FP32
    // Front-panel prefix accepts FP (canonical), TT and P (legacy aliases)
    static validate (id:string) : {major: string, minor: number} | undefined {
        id = id.toUpperCase()
        const [m,...g] = id.match(/^(([A-L])([1-8])|(FP|TT|P)([1-9]|[12]\d|3[0-2]))$/)||[]
        if (m) {
            // ex: A1
            return g[1] ? {
                major: g[1],
                minor: +g[2],
            }
            // ex: FP1
            : {
                major: g[3],
                minor: +g[4],
            }
        }
    }
}

/**
 * FRONT-PANEL range — a contiguous block of FP-encoded ports mapped onto a
 * range of physical matrix channels.
 *
 * Encoded form: ports addressed via FP are stored as negative integers.
 * For a range, |port| values from `start` through `end` (inclusive) are valid.
 *
 * `offset` is the physical matrix channel of FP-index 1 within this range
 * (0-indexed). For most arches `offset === start - 1` (the encoded number
 * equals matrix-channel + 1). PP32R is the exception — it inherits PP64R's
 * encoding (start=17) while having a smaller matrix (offset=0), so FP1 (-17)
 * physically refers to matrix channel 0 (= A1).
 */
export interface FPRange {
    offset: number
    start: number
    end: number
}

/**
 * BACK-PANEL range — one bank's contiguous span of matrix channels.
 * Encoded port for `Xn` (1-indexed minor) is `start + (n - 1)`.
 */
export interface BPRange {
    bank: string
    start: number
    end: number
}

export interface IPortArch {
    // Cross-point routing matrix size (informational; PP32R=16, PP64R=32, PP128R=64, PP200R=100)
    matrix: 16 | 32 | 64 | 100
    // Bank labels including 'FP' if FP ports are present — UI affordance
    banks: string[]
    bp: BPRange[]
    // Accepted FP ranges; storage/parser validation uses this full topology.
    fp: FPRange[]
    // UI-active FP ranges; reserved ranges may be accepted but hidden.
    fpActive: FPRange[]
    isConflict (a?: number|string, b?: number|string) : boolean
    isValid (port: number|string) : boolean
    toPortId (port:number) : PortId | undefined
    // Accepts a port ID string or a numeric channel; returns the validated
    // channel number, or undefined if the input is malformed/out-of-range.
    fromPortId (id:string|number) : number | undefined
    fpCount () : number
    bpCount () : number
    formatHint () : string
}

type TModelRev = 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G' | 'H' | 'J' | 'K' | 'L' | 'M'
    | 'N' | 'P' | 'R' | 'S' | 'T' | 'U' | 'V' | 'W' | 'X' | 'Y' | 'Z'
type TModelName = 'PP32R' | 'PP64R' | 'PP128R' | 'PP200R'

/**
 * PP product-model metadata
 */
export interface IModel {
    name: TModelName
    rev?: TModelRev
}

export interface IArch {
    model: IModel
    ports: IPortArch
}

// HELPER: count slots in a range list
const rangeSlots = (rs: {start:number,end:number}[]) =>
    rs.reduce((s, r) => s + (r.end - r.start + 1), 0)

// Hardware invariant: every PP arch packs BP channels into 8-wide banks.
export const BANK_N = 8
// Standard FP block size — one FP "bank" is always 16 ports. PP200R is the
// only arch with two such blocks (active + reserved).
export const CHANS_FP_N = 16
export const CHANS_PP32R_MATRIX_N = 16
export const CHANS_PP32R_BP_N = 16
export const CHANS_PP64R_MATRIX_N = 32
export const CHANS_PP64R_BP_N = 32
export const CHANS_PP128R_MATRIX_N = 64
export const CHANS_PP128R_BP_N = 48
export const CHANS_PP200R_MATRIX_N = 100
export const CHANS_PP200R_BP_N = 96

// Generate sequential BPRange[] for `channels` total BP channels (must be a
// multiple of BANK_N). Matrix size may exceed BP channels (PP128R: BP=48,
// matrix=64; PP200R: BP=96, matrix=100) — pass the BP count explicitly.
function makeBanks (channels: number) : BPRange[] {
    return Array.from({length: channels / BANK_N}, (_, i) => ({
        bank: String.fromCharCode(65 + i),
        start: i * BANK_N,
        end:   i * BANK_N + (BANK_N - 1),
    }))
}

/**
 * Build an IPortArch from declarative range tables. All four arches share
 * the same algorithms — only the data differs.
 */
function makePortArch (
    matrix: 16 | 32 | 64 | 100,
    bp: BPRange[],
    fp: FPRange[],
    fpActive: FPRange[] = fp,
    fpLegacyPositive: FPRange[] = [],
) : IPortArch {
    const banks = [...bp.map(r => r.bank), ...(fpActive.length ? ['FP'] : [])]

    // Map an FP-encoded |port| to its physical matrix channel (0-indexed).
    // Bridges the FP encoding asymmetry (PP32R inherits PP64R offset; see FPRange).
    const fpToPhysical = (encoded: number) : number | undefined => {
        const r = fp.find(r => encoded >= r.start && encoded <= r.end)
        return r ? (encoded - r.start) + r.offset : undefined
    }

    const isBP = (p: number) => bp.some(r => p >= r.start && p <= r.end)
    const isLegacyPositiveFP = (p: number) =>
        fpLegacyPositive.some(r => p >= r.start && p <= r.end)

    // Walks ranges in declaration order; FP numbering is continuous across them.
    const toFpPortId = (encoded: number, ranges: FPRange[]) : PortId | undefined => {
        let consumed = 0
        for (const r of ranges) {
            if (encoded >= r.start && encoded <= r.end) {
                const localIdx = encoded - r.start + 1
                return `FP${consumed + localIdx}`
            }
            consumed += r.end - r.start + 1
        }
    }

    const port: IPortArch = {
        matrix,
        banks,
        bp,
        fp,
        fpActive,

        fpCount: () => rangeSlots(fp),
        bpCount: () => rangeSlots(bp),

        formatHint: () => {
            const lastBank = bp.at(-1)?.bank ?? 'A'
            const fpMax = rangeSlots(fp)
            return fpMax > 0
                ? `'[A-${lastBank}][1-8]|FP[1-${fpMax}]'`
                : `'[A-${lastBank}][1-8]'`
        },

        isConflict: (a, b) => {
            if (a == null || b == null) return false
            const $a = port.fromPortId(a)
            const $b = port.fromPortId(b)
            if ($a == null || $b == null) return false
            const physA = $a < 0 ? fpToPhysical(Math.abs($a)) : $a
            const physB = $b < 0 ? fpToPhysical(Math.abs($b)) : $b
            return physA === physB
        },

        isValid: ($port) => {
            const p = port.fromPortId($port)
            if (p == null) return false
            if (p < 0) {
                const encoded = Math.abs(p)
                return fp.some(r => encoded >= r.start && encoded <= r.end)
            }
            return isBP(p) || isLegacyPositiveFP(p)
        },

        toPortId: (p) => {
            if (p < 0) {
                const portId = toFpPortId(Math.abs(p), fp)
                if (portId) return portId
                D('PORT', p)
                return
            }
            const r = bp.find(r => p >= r.start && p <= r.end)
            if (r) {
                return `${r.bank}${p - r.start + 1}`
            }
            const legacyPortId = toFpPortId(p, fpLegacyPositive)
            if (legacyPortId) return legacyPortId
            D('PORT', p)
        },

        fromPortId: (id) => {
            // Numeric input: validate that this channel is reachable on this arch
            if (typeof id === 'number') {
                if (!Number.isInteger(id)) return undefined
                if (id < 0) {
                    const encoded = Math.abs(id)
                    return fp.some(r => encoded >= r.start && encoded <= r.end) ? id : undefined
                }
                return isBP(id) || isLegacyPositiveFP(id) ? id : undefined
            }
            // String input: parse via shared validator
            const v = ChannelPort.validate(id)
            if (!v) return undefined
            const {major, minor} = v
            if (major === 'FP' || major === 'P' || major === 'TT') {
                // Walk FP ranges; fp_index `minor` may live in any of them
                let consumed = 0
                for (const r of fp) {
                    const slots = r.end - r.start + 1
                    if (minor <= consumed + slots) {
                        const localIdx = minor - consumed
                        return -(localIdx - 1 + r.start)
                    }
                    consumed += slots
                }
                return undefined
            }
            const r = bp.find(r => r.bank === major && minor <= (r.end - r.start + 1))
            return r ? (minor - 1) + r.start : undefined
        },
    }
    return port
}

// PP32R: half-width PP64R sibling — matrix 16, banks A-B, FP1-FP16 fully
// overlap A1-B8. Note: FP encoding inherits PP64R offset (start=17) while
// matrix is 16 — `offset:0` captures this asymmetry.
export const ArchPP32A:IArch = {
    model: { name: 'PP32R' },
    ports: makePortArch(
        CHANS_PP32R_MATRIX_N,
        makeBanks(CHANS_PP32R_BP_N),
        [{ offset: 0, start: CHANS_FP_N + 1, end: CHANS_FP_N * 2 }],
    ),
}

// PP64R: 4 BP banks (A-D, 32 channels), FP1-FP16 overlaps C1-D8
export const ArchPP64A:IArch = {
    model: { name: 'PP64R' },
    ports: makePortArch(
        CHANS_PP64R_MATRIX_N,
        makeBanks(CHANS_PP64R_BP_N),
        [{ offset: CHANS_FP_N, start: CHANS_FP_N + 1, end: CHANS_PP64R_MATRIX_N }],
    ),
}

// PP128R: 6 BP banks (A-F, 48 channels) + FP1-FP16 in a separate matrix zone
// (channels 48-63). No overlap between FP and BP.
const PP128_FP = {
    offset: CHANS_PP128R_BP_N,
    start: CHANS_PP128R_BP_N + 1,
    end: CHANS_PP128R_MATRIX_N,
}
// Back-compat: pre-refactor PP128R sessions stored FP as positive matrix
// indices 48-63. Treated as FP1-FP16 (toPortId(48) === 'FP1').
const PP128_FP_LEGACY_POSITIVE = {
    offset: CHANS_PP128R_BP_N,
    start: CHANS_PP128R_BP_N,
    end: CHANS_PP128R_MATRIX_N - 1,
}
export const ArchPP128A:IArch = {
    model: { name: 'PP128R' },
    ports: makePortArch(
        CHANS_PP128R_MATRIX_N,
        makeBanks(CHANS_PP128R_BP_N),
        [PP128_FP],
        [PP128_FP],
        [PP128_FP_LEGACY_POSITIVE],
    ),
}

// PP200R: 12 BP banks (A-L, 96 channels) + FP1-FP16 mapped onto matrix
// channels 84-99. FP1-FP12 overlap K5-L8; FP13-FP16 (channels 96-99) are
// front-panel-only. FP17-FP32 are reserved/inactive but accepted by storage.
const PP200_FP_RESERVED = { offset: 68, start: 69, end: 84 }
const PP200_FP_ACTIVE = {
    offset: PP200_FP_RESERVED.end,
    start: PP200_FP_RESERVED.end + 1,
    end: CHANS_PP200R_MATRIX_N,
}
export const ArchPP200A:IArch = {
    model: { name: 'PP200R' },
    ports: makePortArch(
        CHANS_PP200R_MATRIX_N,
        makeBanks(CHANS_PP200R_BP_N),
        [PP200_FP_ACTIVE, PP200_FP_RESERVED],
        [PP200_FP_ACTIVE],
    ),
}

export class Arch {
    static from (model: TModelName|string, rev?: string) : IArch | undefined {
        switch (model.toUpperCase()) {
            case 'PP32R':  return ArchPP32A
            case 'PP64R':  return ArchPP64A
            case 'PP128R': return ArchPP128A
            case 'PP200R': return ArchPP200A
        }
    }

    static same (a:IArch, b:IArch) : boolean {
        return a.model.name === b.model.name
    }
}
